/**
 * Protected Route Component
 * Wraps routes that require authentication and optional role-based access
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: UserRole | UserRole[];
  requireSchoolAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole,
  requireSchoolAdmin = false,
}) => {
  const { user, isAuthenticated, loading, hasRole, isSchoolAdmin } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Superusers and staff bypass all role/permission checks
  const isSuperuserOrStaff = user?.is_superuser || user?.is_staff;

  // Check role-based access if required (bypass for superusers/staff)
  if (requireRole && !isSuperuserOrStaff) {
    const hasRequiredRole = hasRole(requireRole);
    if (!hasRequiredRole) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            You do not have permission to access this page.
          </Typography>
        </Box>
      );
    }
  }

  // Check school admin access if required (bypass for superusers/staff)
  if (requireSchoolAdmin && !isSchoolAdmin && !isSuperuserOrStaff) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" color="textSecondary">
          This page is only accessible to school administrators.
        </Typography>
      </Box>
    );
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default ProtectedRoute;
